import React from 'react';

interface ListOptions<T> {
  items: T[];
  getKey: (item: T) => string;
  defaultSelectedKey?: string;
  selectedKey?: string;
  enableAutoSelect?: boolean;
  onChange?: (item: T) => void;
}

export interface ListState<T> {
  items: T[];
  focusedKey: string;
  selectedKey: string;
  setFocusedKey: (key: string) => void;
  setSelectedKey: (key: string) => void;
  getKeys: () => string[];
  getSelectedItem: () => T | undefined;
}

export function useListState<T>(options: ListOptions<T>): ListState<T> {
  const { items, getKey, defaultSelectedKey, selectedKey: selectedKeyProp, onChange } = options;

  const [internalSelectedKey, setInternalSelectedKey] = React.useState<string>(defaultSelectedKey!);

  const selectedKey = selectedKeyProp !== undefined ? selectedKeyProp : internalSelectedKey;

  const [focusedKey, setFocusedKey] = React.useState<string>(selectedKey);

  const getKeys = () => {
    return items.map(item => getKey(item));
  };

  const getSelectedItem = () => {
    return items.find(item => getKey(item) === selectedKey);
  };

  const setSelectedKey = (newKey: string) => {
    setInternalSelectedKey(newKey);
    if (onChange && newKey !== selectedKey) {
      const newItem = items.find(item => getKey(item) === newKey);
      onChange(newItem!);
    }
  };

  return {
    items,
    focusedKey,
    selectedKey,
    getKeys,
    getSelectedItem,
    setFocusedKey,
    setSelectedKey,
  };
}
